import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ContactService } from 'services/contact/contact';
import { SingleTicket } from 'utils/api/models/tickets/singleTicket';
import AccountFilterTable, { ISelectedAccount } from 'views/common/AccountFilterTable';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { AccountsService } from 'services/accounts/accounts';
import { translate } from 'components/Translation';
import { AccountCell, TextCell } from 'components/Cells/Cells';
import { useDispatch } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { finalize } from 'rxjs/operators';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import Business from '@material-ui/icons/Business';
import Dropdown from 'components/Dropdown/Dropdown';
import { MoreVert } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/listOfComplaints/listOfComplaintsStyle';
import ComplaintDetailsDialog from './subcomponents/ComplaintDetailsDialog';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomDatePicker from 'components/CustomDatePicker/CustomDatePicker';
import { validateDateString } from 'utils/date/validateDateString';
import { now } from 'utils/date/getDate';
import { FilterValue } from 'react-table';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import { useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';

interface IPeriod {
	startDate: Moment | null;
	endDate: Moment | null;
}

const LAST_DAYS_NUMBER = 30;

// @ts-ignore
const useStyles = makeStyles(styles);

const ListOfComplaints = () => {
	const [tickets, setTickets] = useState<SingleTicket[]>([]);
	const [accounts, setAccounts] = useState<SingleCorpAccount[]>([]);
	const [loadingAccounts, setLoadingAccounts] = useState<boolean>(true);
	const [selectedAccount, setSelectedAccount] = useState<ISelectedAccount | null>(null);
	const [searchInputValue, setSearchInputValue] = useState<string>('');
	const [selectedTicketId, setSelectedTicketId] = useState<string>('');
	const [period, setPeriod] = useState<IPeriod>({
		startDate: moment()
			.subtract(LAST_DAYS_NUMBER, 'd')
			.startOf('day'),
		endDate: moment().endOf('day'),
	});
	const [filters, setFilters] = useState<FilterValue>({
		period: ['lastDays'],
		category: [],
		subcategory: [],
		type: [],
	});
	const [filteredTickets, setFilteredTickets] = useState<SingleTicket[]>([]);

	const dispatch = useDispatch();
	const classes = useStyles();
	const { search } = useLocation();

	const filter = useCallback(
		_.debounce(() => {
			const _searchInputValue = searchInputValue.toLowerCase();
			const filtered = tickets.filter((request) => {
				return [
					[filters.category.length > 0, filters.category.some((cat: string) => request.category === cat)],
					[
						filters.period.length > 0,
						period.startDate &&
							period.endDate &&
							moment(request.createdAt) >= period.startDate &&
							moment(request.createdAt) <= period.endDate,
					],
					[
						filters.subcategory.length > 0,
						filters.subcategory.some((sc: string) => request.subcategory === sc),
					],
					[filters.type.length > 0, filters.type.some((type: string) => type === request.type)],
					[
						_searchInputValue !== '',
						['category', 'type', 'subcategory', 'externalId']
							.filter((field) => !!request[field])
							.some((field) => {
								return request[field]
									.toString()
									.toLowerCase()
									.includes(_searchInputValue);
							}),
					],
				]
					.filter((check) => check[0])
					.every((check) => check[1]);
			});
			setFilteredTickets(filtered);
		}, 100),
		[searchInputValue, filters, selectedAccount, tickets, period]
	);

	useEffect(() => {
		filter();
	}, [filter]);

	useEffect(() => {
		const incidentId = DOMPurify.sanitize(new URLSearchParams(search).get('incidentId') ?? '');
		if (incidentId) {
			/** if any incident Id is there, put it in the search item and clear all filters */
			setSearchInputValue(incidentId);
			setFilters({
				period: [],
				category: [],
				subcategory: [],
				type: [],
			});
		}
	}, [search]);

	const accountsMap = useMemo(
		() => accounts.reduce((acc, account) => ({ ...acc, [account.accountId]: account }), {}),
		[accounts]
	);

	useEffect(() => {
		setLoadingAccounts(true);
		const subscription = AccountsService.getCorpAccountsForCurrentUser()
			.pipe(finalize(() => setLoadingAccounts(false)))
			.subscribe(({ accounts }) => {
				setAccounts(accounts);
			});
		return () => {
			subscription.unsubscribe();
		};
	}, []);

	useEffect(() => {
		if (!loadingAccounts) {
			dispatch(showLoadingIndicator());
			const subscription = ContactService.getTickets(
				selectedAccount?.id && accounts.length > 1 ? Number(selectedAccount.id) : null
			)
				.pipe(finalize(() => dispatch(hideLoadingIndicator())))
				.subscribe(({ tickets }) => setTickets(tickets));
			return () => {
				subscription.unsubscribe();
			};
		}
	}, [selectedAccount, loadingAccounts, accounts, dispatch]);

	const dateChanged = (date: string | moment.Moment, key: 'startDate' | 'endDate'): void => {
		if (typeof date === 'string') {
			if (validateDateString(date)) {
				const updatedDate = moment(date);
				if (updatedDate.isValid()) {
					setPeriod((period) => ({ ...period, [key]: updatedDate }));
				}
			}
		} else {
			setPeriod((period) => ({ ...period, [key]: date }));
		}
	};

	const prepareDate = (value: any[]) => {
		if (!value.length) {
			setPeriod({ startDate: null, endDate: moment().endOf('day') });
		} else if (value[0] === 'lastDays') {
			setPeriod({
				startDate: moment()
					.subtract(LAST_DAYS_NUMBER, 'd')
					.startOf('day'),
				endDate: moment().endOf('day'),
			});
		}
	};

	return (
		<div style={{ width: '100%' }}>
			<AccountFilterTable
				accounts={accounts}
				setAccount={(value) => setSelectedAccount(value)}
				selectedAccount={selectedAccount}
				filterHeader={''}
				filtersValue={filters}
				filters={[
					{
						title: translate('payments.bills.period'),
						name: 'period',
						type: 'one',
						elements: [
							{
								title: translate('numbers.usage.customPeriod'),
								value: 'custom',
								show: (
									<>
										<div className={classes.datePicker}>
											<CustomDatePicker
												timeFormat={false}
												value={period.startDate}
												onChange={(date) => dateChanged(date, 'startDate')}
												isValidDate={(currentDate) => currentDate.isSameOrBefore(now)}
												inputProps={{
													placeholder: translate('activities.details.chooseStartDate'),
												}}
											/>
										</div>
										<div className={classes.datePicker}>
											<CustomDatePicker
												timeFormat={false}
												value={period.endDate}
												onChange={(date) => dateChanged(date, 'endDate')}
												isValidDate={(currentDate) => {
													if (period.startDate) {
														return (
															period.startDate?.isBefore(currentDate, 'day') &&
															currentDate.isSameOrBefore(now)
														);
													} else {
														return false;
													}
												}}
												inputProps={{}}
											/>
										</div>
									</>
								),
							},
							{
								title: translate('last.days', LAST_DAYS_NUMBER),
								value: 'lastDays',
							},
						],
					},
					{
						title: translate('complaints.category'),
						name: 'category',
						type: 'multiple',
						elements: [...new Set(tickets.map((request) => request.category))].map((value) => ({
							title: value,
							value,
						})),
					},
					{
						title: translate('complaints.subcategory'),
						name: 'subcategory',
						type: 'multiple',
						elements: [...new Set(tickets.map((request) => request.subcategory))].map((value) => ({
							title: value,
							value,
						})),
					},
					{
						title: translate('table.type'),
						name: 'type',
						type: 'multiple',
						elements: [...new Set(tickets.map((request) => request.type))].map((value) => ({
							title: value,
							value,
						})),
					},
				]}
				onFilterChange={(name, value) => {
					setFilters((filters: any) => ({ ...filters, [name]: value }));
					if (name === 'period') prepareDate(value);
				}}
				data={filteredTickets}
				selectPanel={{}}
				columns={[
					{
						Header: translate('complaints.number'),
						Cell: ({ original }: { original: SingleTicket }) => (
							<TextCell bold text={original.externalId} />
						),
						accessor: 'externalId',
					},
					!selectedAccount?.id && {
						Header: translate('table.organization.name'),
						Cell: ({ original }: { original: SingleTicket }) => {
							const account = accountsMap[original.createdBy];
							return account ? <TextCell text={account.accountName} /> : null;
						},
						accessor: 'account.accountName',
					},
					{
						Header: translate('complaints.createdAt'),
						Cell: ({ original }: { original: SingleTicket }) => <TextCell text={original.createdAt} />,
						accessor: 'createdAt',
					},
					{
						Header: translate('complaints.category'),
						Cell: ({ original }: { original: SingleTicket }) => <TextCell text={original.category} />,
						accessor: 'category',
						width: 200,
					},
					{
						Header: translate('complaints.subcategory'),
						Cell: ({ original }: { original: SingleTicket }) => <TextCell text={original.subcategory} />,
						accessor: 'subcategory',
						width: 130,
					},
					{
						Header: translate('complaints.type'),
						Cell: ({ original }: { original: SingleTicket }) => <TextCell text={original.type} />,
						accessor: 'type',
						width: 200,
					},
					{
						width: 84,
						Cell: ({ original }: { original: SingleTicket }) => (
							<div className={classes.moreWrapper}>
								<Dropdown
									menu={<MoreVert />}
									items={[
										{
											text: translate('complaints.complaint.details'),
											onClick: () => {
												setSelectedTicketId(original.externalId);
											},
											hidden: false,
										},
									]}
									anchorOrigin={{
										vertical: 'bottom',
										horizontal: 'right',
									}}
									transformOrigin={{
										vertical: 'top',
										horizontal: 'right',
									}}
								/>
							</div>
						),
					},
				].filter(Boolean)}
				setSearchInputValue={setSearchInputValue}
				searchInputValue={searchInputValue}
				loading={false}
			/>
			<ConditionalRender
				show={!!selectedTicketId}
				onTrue={() => (
					<ComplaintDetailsDialog ticketId={selectedTicketId} hideDialog={() => setSelectedTicketId('')} />
				)}
			/>
		</div>
	);
};

export default ListOfComplaints;
